<script setup lang="ts">
import { api } from '@/api';
import type { CertificateInfoDto, SetKestrelCertificateSettingsRequest } from '@/api/generator';
import VeeFileInput from '@/components/forms/FileInput/VeeFileInput.vue';
import VeePasswordField from '@/components/forms/PasswordField/VeePasswordField.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { formBackendErrorHandler } from '@/errorHandling/errorHandler.ts';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';

const { t } = useTranslation();

const emit = defineEmits<{
  saved: [value: CertificateInfoDto];
  canceled: [];
}>();

type SetKestrelCertificateSettingsRequestBrowserSide = {
  file: File | undefined;
  password: string | undefined;
};

const { form, showModalOnChanges } = useVeeForm<SetKestrelCertificateSettingsRequestBrowserSide>({
  mode: 'edit',
  options: {
    initialValues: {
      file: undefined,
      password: undefined,
    },
  },
});
const { handleSubmit, isSubmitting, isValidating, meta, setFieldError } = form;

const isLoading = computed(() => isValidating.value || isSubmitting.value);

const submit = handleSubmit(async (data) => {
  if (data.file) {
    const requestData: SetKestrelCertificateSettingsRequest = {
      file: data.file,
      password: data.password,
    };
    const { success, result } = await api.tenantsApi.setKestrelCertificateSettings(requestData, [
      formBackendErrorHandler(form),
    ]);
    if (success && result) {
      emit('saved', result);
    }
  } else {
    setFieldError('file', t('Validation.PropertyRequired'));
  }
});

async function cancel() {
  if (meta.value.dirty && showModalOnChanges) {
    await showModalOnChanges(() => {
      emit('canceled');
    });
  } else {
    emit('canceled');
  }
}
</script>

<template>
  <v-sheet class="px-8 mt-10">
    <form @submit="submit">
      <v-container :fluid="true">
        <v-row>
          <v-col cols="12" md="6">
            <p class="mt-4 mb-2">
              {{ $t('UploadNewSSLCertificateMsg') }}
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-row>
              <v-col>
                <VeeFileInput
                  name="file"
                  mode="edit"
                  :label="$t('UploadSSLCertificate')"
                  :infoMessage="$t('FormatPKCS12AndFileTypePfx')"
                  accept=".pfx"
                  :placeholder="$t('Required')"
                />
              </v-col>
              <v-col>
                <VeePasswordField
                  name="password"
                  mode="edit"
                  :label="$t('Password')"
                  :infoMessage="$t('InsertThePasswordYourSslCertificateHasBeenEncryptedWith')"
                  :placeholder="$t('Required')"
                />
              </v-col>
              <v-col class="text-right align-center justify-end d-flex">
                <div class="d-flex flex-row align-content-end">
                  <TextVBtn
                    class="mr-4"
                    :text="$t('Cancel')"
                    :disabled="isLoading"
                    @click="cancel"
                  />
                  <VeeSubmitButton
                    :text="$t('Save')"
                    class="px-16"
                    :loading="isLoading"
                    :disabled="isLoading"
                  />
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </form>
  </v-sheet>
</template>
